import { Paper, Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ReactNode } from 'react';
import { TransceptaLogoWhite } from '../../../ui/Logos/TransceptaLogoWhite';
import { TransceptaLogoDarkBlue } from '../../../ui/Logos/TransceptaLogoDarkBlue';

interface IFormTemplateProps {
    mainContent: ReactNode;
    marketingContent?: ReactNode;
    testId?: string;
    verticalLayout?: boolean;
}

/**
 * A reusable template for login forms that provides consistent layout with Transcepta branding.
 * Displays the Transcepta logo above a paper container with optional marketing content sidebar.
 * Set verticalLayout to true to display the marketing content above the main content.
 */
export const FormTemplate = ({ mainContent, marketingContent, testId, verticalLayout = false }: IFormTemplateProps) => {
    const theme = useTheme();
    const isVertical = verticalLayout && marketingContent;

    return (
        <Stack justifyContent="center" alignItems="center" sx={{ minHeight: '100vh', width: '100%', p: 2 }} spacing={3}>
            {theme.palette.mode === 'dark' ? <TransceptaLogoWhite /> : <TransceptaLogoDarkBlue />}
            <Paper
                component="main"
                sx={{
                    minWidth: marketingContent ? (isVertical ? 360 : 460) : 360,
                    maxWidth: marketingContent ? (isVertical ? 400 : 560) : 400,
                    minHeight: marketingContent ? (isVertical ? 260 : 200) : 260,
                    maxHeight: marketingContent ? (isVertical ? 460 : 300) : 460,
                    p: 3,
                    display: 'flex',
                }}
            >
                <Stack
                    direction={isVertical ? 'column' : 'row'}
                    spacing={isVertical ? 2 : 3}
                    sx={{ width: '100%', flex: 1, minHeight: 0 }}
                >
                    {marketingContent && (
                        <Stack flex={1} justifyContent="center" alignItems="center">
                            {marketingContent}
                        </Stack>
                    )}
                    <Stack flex={1} justifyContent="center" alignItems="center" data-testid={testId}>
                        {mainContent}
                    </Stack>
                </Stack>
            </Paper>
        </Stack>
    );
};
